"use client";

import type { Attachment, UIMessage } from "ai";
import cx from "classnames";
import {
  AGENT_MODELS,
  modelByKey,
  modelKeyFromTier,
  type AgentModelKey,
} from "@/lib/ai/agent-models";
import type React from "react";
import {
  useRef,
  useEffect,
  useState,
  useCallback,
  type Dispatch,
  type SetStateAction,
  type ChangeEvent,
  type MutableRefObject,
  memo,
} from "react";
import { toast } from "sonner";
import { useWindowSize } from "usehooks-ts";

import { ArrowUpIcon, PaperclipIcon, StopIcon } from "./icons";
import { PreviewAttachment } from "./preview-attachment";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { DataSelector, SelectedDataChips } from "./data/DataSelector";
import { SkillsButton } from "./skills-button";
import { Textarea } from "./ui/textarea";
import { SuggestedActions } from "./suggested-actions";
import { useAuth } from "@/contexts/AuthContext";

import { useScrollToBottom } from "@/hooks/use-scroll-to-bottom";
import { useArtifact } from "@/hooks/use-artifact";
import { API_BASE_URL } from "@/lib/config";
import { apiClient } from "@/lib/api-client";
import type { UseChatHelpers } from "@ai-sdk/react";
import type { VisibilityType } from "./visibility-selector";
import equal from "fast-deep-equal";
import { AnimatePresence, motion } from "framer-motion";
import { useRouter } from "next/navigation";
import { AlertTriangle, ArrowDown, ArrowLeft, Check, ChevronDown, ImageOff, Loader2, Mic, Sparkles, X } from "lucide-react";
import Image from "next/image";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "./ui/dialog";
import { AuthModal } from "./auth/auth-modal";
import { LiveWaveform } from "./ui/live-waveform";
import type { DataSource, RequestedDataSource } from "@/lib/data-catalog";
import { useUserUsage } from "@/hooks/useUserUsage";
import { useIsMobile } from "@/hooks/use-is-mobile";
import { debugLog, createChannelLogger } from "@/lib/debug-log";
import { telemetry } from "@/lib/telemetry";
import { formatUsd, getUsageUsdInfo } from "@/lib/usage-display";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "./ui/tooltip";

// Replay-gate diagnostics: writes to frontend_replay_debug.txt. Used to
// trace whether the `xynth:prefill-input` event from <DemoReplayInput>
// is being received by this component's listener and whether setDraft
// actually sticks. Cleared via DELETE /api/debug-log?channel=replay_debug.
const replayLog = createChannelLogger("replay_debug");

// Data selector replaces the old generic tools selector. `dataRef.current`
// is a snapshot of the user's currently selected data sources; the chat
// container reads it once on submit, sends it as `data_requested`, and
// resets the ref. See @/lib/data-catalog for the catalog definition.

function PureMultimodalInput({
  chatId,
  status,
  stop,
  attachments,
  setAttachments,
  onRemoveAttachment,
  messages,
  setMessages,
  append,
  handleSubmit,
  className,
  selectedVisibilityType,
  dataRef,
  isRerunning = false,
  isInitializing = false,
  editingCell,
  onClearEditingCell,
  // Workflow variant props
  variant = "default",
  placeholderTexts,
  promptPills,
  onPromptPillClick,
  onStandaloneSubmit,
  hideData = false,
  deepResearchRef,
  deepResearch: deepResearchProp,
  modelTierRef,
  modelTier: modelTierProp,
  onModelTierChange,
  selectedModelKey: selectedModelKeyProp,
  onModelChange,
  modelOverrideRef,
  activeSkillIdRef,
  initialActiveSkill,
  onDeepResearchChange,
  isAgentProcessing = false,
  onStopped,
  forceReadOnly = false,
  highlightSend = false,
  onCmdEnter,
}: {
  chatId: string;
  input: UseChatHelpers["input"];
  setInput: UseChatHelpers["setInput"];
  status?: UseChatHelpers["status"];
  stop?: () => void;
  attachments: Array<Attachment>;
  setAttachments: Dispatch<SetStateAction<Array<Attachment>>>;
  onRemoveAttachment?: (attachment: Attachment) => void;
  messages?: Array<UIMessage>;
  setMessages?: UseChatHelpers["setMessages"];
  append?: UseChatHelpers["append"];
  handleSubmit?: UseChatHelpers["handleSubmit"];
  className?: string;
  selectedVisibilityType?: VisibilityType;
  dataRef?: React.MutableRefObject<Array<RequestedDataSource>>;
  isRerunning?: boolean;
  isInitializing?: boolean;
  editingCell?: {
    index: number;
    purpose: string;
  } | null;
  onClearEditingCell?: () => void;
  // Workflow variant props
  variant?: "default" | "workflow";
  placeholderTexts?: string[];
  promptPills?: string[];
  onPromptPillClick?: (prompt: string) => void;
  onStandaloneSubmit?: (
    message: string,
    attachments: Array<Attachment>
  ) => void;
  hideData?: boolean;
  deepResearchRef?: MutableRefObject<boolean>;
  deepResearch?: boolean;
  modelTierRef?: MutableRefObject<string>;
  modelTier?: "basic" | "max";
  onModelTierChange?: (tier: "basic" | "max") => void;
  selectedModelKey?: AgentModelKey;
  onModelChange?: (key: AgentModelKey) => void;
  modelOverrideRef?: MutableRefObject<string | undefined>;
  /** Skill launch (no-blob): the composer sets this to the active skill id(s)
   *  right before append; <Chat> reads-and-clears it into `skill_ids`. */
  activeSkillIdRef?: MutableRefObject<string[] | undefined>;
  /** Skill launch (no-blob): arm the chat bar with an active skill on mount
   *  (from a `?skill=` deep link or Run-now handoff). */
  initialActiveSkill?: { id: string; name: string };
  onDeepResearchChange?: (value: boolean) => void;
  isAgentProcessing?: boolean;
  onStopped?: () => void;
  /**
   * When true, the textarea becomes non-editable. Used by the demo
   * replay input bar to lock a pre-filled `next_user_message` so the
   * viewer's only action is clicking Send — they can't type over the
   * replay's canned next user message. Still allows focus + copy.
   */
  forceReadOnly?: boolean;
  /**
   * When true, the Send button gets a "look here" treatment (white
   * glow + gentle pulse). Used in tandem with `forceReadOnly` during
   * a demo replay's multi-turn handoff so it's obvious the viewer
   * should click Send to continue.
   */
  highlightSend?: boolean;
  /**
   * Optional Cmd/Ctrl+Enter shortcut. When set, pressing the combo
   * fires this callback INSTEAD of submitting the message. Receives
   * the current trimmed draft text so the handler can forward it
   * (e.g. as a query-param override for the destination route).
   * Lets the homepage composer route the user to the clean replay
   * tour (`/replay/<id>/clean?initial_prompt=…`) without losing the
   * regular Enter→submit behaviour. Plain Enter is unaffected.
   */
  onCmdEnter?: (draftText: string) => void;
}) {
  const isWorkflow = variant === "workflow";
  const effectiveStatus = status ?? "ready";
  const effectiveMessages = messages ?? [];
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { width } = useWindowSize();
  const { user } = useAuth();
  const { data: usageData, refreshUsage } = useUserUsage();
  const router = useRouter();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [preventFocus, setPreventFocus] = useState(true);
  // Model tier selector — use lifted props if available, otherwise local state
  const [localModelTier, setLocalModelTier] = useState<"basic" | "max">("basic");
  const [localDeepResearch, setLocalDeepResearch] = useState(false);
  const modelTier = modelTierProp ?? localModelTier;
  const deepResearch = deepResearchProp ?? localDeepResearch;
  const setModelTier = onModelTierChange ?? setLocalModelTier;
  const setDeepResearch = onDeepResearchChange ?? setLocalDeepResearch;
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioChunksRef = useRef<Array<BlobPart>>([]);
  const discardRecordingRef = useRef(false);
  const [isRecordingAudio, setIsRecordingAudio] = useState(false);
  const [isTranscribingAudio, setIsTranscribingAudio] = useState(false);
  const isMobile = useIsMobile();

  // Prevent auto-focus on mount for mobile (prevents keyboard popup)
  useEffect(() => {
    const timer = setTimeout(() => setPreventFocus(false), 100);
    return () => clearTimeout(timer);
  }, []);

  // Data selection state. The DataSelector dropdown writes here and
  // mirrors it into `dataRef.current` so the parent <Chat> can read
  // the latest selection synchronously in `experimental_prepareRequestBody`.
  const [selectedData, setSelectedData] = useState<Array<RequestedDataSource>>(
    []
  );
  const updateDataRef = useCallback(
    (sources: Array<RequestedDataSource>) => {
      if (dataRef) {
        dataRef.current = sources;
      }
    },
    [dataRef]
  );
  const toggleData = useCallback(
    (source: DataSource) => {
      setSelectedData((prev) => {
        const exists = prev.some((s) => s.id === source.id);
        if (exists) {
          const next = prev.filter((s) => s.id !== source.id);
          updateDataRef(next);
          return next;
        }
        if (prev.length >= 8) {
          toast.error("You can add up to 8 data sources per message");
          updateDataRef(prev);
          return prev;
        }
        const next: Array<RequestedDataSource> = [
          ...prev,
          {
            id: source.id,
            name: source.name,
            sources: source.tool_names,
            ...(source.fmp_endpoint
              ? { fmp_endpoint: source.fmp_endpoint }
              : {}),
            ...(source.alerts_scope
              ? { alerts_scope: source.alerts_scope }
              : {}),
          },
        ];
        updateDataRef(next);
        return next;
      });
    },
    [updateDataRef]
  );
  const removeData = useCallback(
    (id: string) => {
      setSelectedData((prev) => {
        const next = prev.filter((s) => s.id !== id);
        updateDataRef(next);
        return next;
      });
    },
    [updateDataRef]
  );

  // Typewriter effect state
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [currentText, setCurrentText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  // Localized draft input state (decoupled from Chat/useChat)
  // Initialize from localStorage so drafts survive refresh/signup
  const [draft, setDraft] = useState(() => {
    if (typeof window === "undefined") return "";
    return localStorage.getItem("xynth-draft-input") || "";
  });
  // Skill launch (no-blob): the active skill armed on the chat bar. When set,
  // the user types their OWN input (mandatory) and we send `skill_id` instead
  // of pasting the instruction blob. A brief modal nudges the user to type.
  // Multiple skills can be stacked on one message (capped). Picking adds to
  // the list; the user types their OWN input (mandatory) and we send
  // `skill_ids` instead of pasting any instruction blob.
  const MAX_SKILLS = 3;
  const [activeSkills, setActiveSkills] = useState<{ id: string; name: string }[]>(
    initialActiveSkill ? [initialActiveSkill] : []
  );
  const [showSkillNudge, setShowSkillNudge] = useState<boolean>(
    Boolean(initialActiveSkill)
  );
  const addActiveSkill = useCallback((s: { id: string; name: string }) => {
    setActiveSkills((prev) => {
      if (prev.some((p) => p.id === s.id)) return prev;
      if (prev.length >= MAX_SKILLS) {
        toast.error(`You can stack up to ${MAX_SKILLS} skills per message.`);
        return prev;
      }
      // eslint-disable-next-line no-console
      console.log("[SKILL-LAUNCH] added skill to composer", { skill: s, count: prev.length + 1 });
      return [...prev, s];
    });
    setShowSkillNudge(true);
  }, []);
  useEffect(() => {
    if (initialActiveSkill) {
      // eslint-disable-next-line no-console
      console.log("[SKILL-LAUNCH] composer armed with active skill", initialActiveSkill);
      setActiveSkills((prev) =>
        prev.some((p) => p.id === initialActiveSkill.id)
          ? prev
          : [...prev, initialActiveSkill]
      );
      setShowSkillNudge(true);
    }
  }, [initialActiveSkill]);
  // Typewriter text options - use custom texts for workflow variant
  const defaultTexts = [
    "find me the highest premium cash-secured puts this week",
    "which tickers have bullish call flow over $5m today",
    "what's the best iv-crush trade for this week's earnings",
    "show me a gamma squeeze setup expiring friday",
    "find me a credit spread with 3:1 reward/risk on spy",
  ];
  const texts = placeholderTexts ?? defaultTexts;
  // The active model: prefer the lifted selection; otherwise derive from the
  // billing tier (back-compat for any caller that only passes modelTier).
  const selectedModelKey: AgentModelKey =
    selectedModelKeyProp ?? modelKeyFromTier(modelTier);
  const hasMidConvoMessages = effectiveMessages.length > 0;
  const usageUsd = getUsageUsdInfo(usageData);
  // Model picker is now a modal (frees up composer toolbar space vs the old
  // inline pills, which pushed the Skills button off-screen on mobile).
  const [modelPickerOpen, setModelPickerOpen] = useState(false);

  const applyModelSelection = useCallback(
    (key: AgentModelKey) => {
      const model = modelByKey(key);
      debugLog("model-apply", { key, before: { modelTier, deepResearch }, hasMidConvoMessages });
      telemetry.emit("chat.tier_change", {
        session_id: chatId,
        user_id: user?.uid,
        selected_pill: model.tier === "max" ? "pro" : "fast",
        selected_model: key,
        selected_model_slug: model.slug,
        previous_model_tier: modelTier,
        previous_deep_research: deepResearch,
        has_mid_convo_messages: hasMidConvoMessages,
      });

      const newModelTier = model.tier;

      if (hasMidConvoMessages) {
        const costNote = newModelTier === "max" ? "Premium cost" : "Low cost";
        toast.info(`Switched to ${model.label}. ${costNote} per message.`, {
          duration: 3000,
        });
      }

      if (modelTierRef) {
        modelTierRef.current = newModelTier;
      }
      if (modelOverrideRef) {
        modelOverrideRef.current = model.slug;
      }
      if (deepResearchRef) {
        deepResearchRef.current = false;
      }

      setModelTier(newModelTier);
      setDeepResearch(false);
      onModelChange?.(key);

      debugLog("model-apply-done", { key, newModelTier, slug: model.slug });
    },
    [chatId, user?.uid, hasMidConvoMessages, setDeepResearch, setModelTier, modelTier, deepResearch, modelTierRef, modelOverrideRef, deepResearchRef, onModelChange]
  );

  const renderTierDropdown = () => {
    const active = modelByKey(selectedModelKey);
    return (
      <Dialog open={modelPickerOpen} onOpenChange={setModelPickerOpen}>
        <DialogTrigger asChild>
          <button
            type="button"
            aria-label={`Model: ${active.label}. Tap to change model`}
            className={cx(
              "flex items-center gap-1.5 px-2.5 py-1 rounded-full shrink-0 select-none transition-colors",
              "bg-muted/40 ring-1 ring-border/50 text-foreground hover:bg-muted/60",
              "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring/60"
            )}
          >
            <Image
              src={active.iconSrc}
              alt=""
              width={14}
              height={14}
              className="shrink-0"
            />
            <span className="text-[11px] font-semibold leading-none whitespace-nowrap">
              {active.shortLabel}
            </span>
            <ChevronDown className="size-3 shrink-0 opacity-60" />
          </button>
        </DialogTrigger>
        <DialogContent className="w-full max-w-[calc(100vw-1.5rem)] gap-0 overflow-hidden rounded-2xl border-white/10 bg-[#0c0c0e] p-0 text-white sm:max-w-md sm:rounded-2xl">
          <div className="border-b border-white/[0.06] bg-gradient-to-b from-white/[0.06] to-transparent px-5 py-4">
            <DialogTitle className="text-[15px] font-semibold tracking-tight text-white">
              Choose your model
            </DialogTitle>
            <p className="mt-1 text-[12px] leading-snug text-white/80">
              Official API pricing per 1M tokens. No markup.
            </p>
          </div>

          <div className="max-h-[70vh] overflow-y-auto p-2.5">
            <div className="flex flex-col gap-2">
              {AGENT_MODELS.map((option) => {
                const isActive = selectedModelKey === option.key;
                const isFreeTier =
                  usageData?.subscription?.tier === "free" &&
                  !usageData?.is_enterprise;
                const geminiLimit = usageUsd.gemini_daily_limit ?? 20;
                const geminiRemaining = Math.max(
                  0,
                  usageUsd.gemini_daily_remaining ??
                    geminiLimit - (usageUsd.gemini_daily_count ?? 0)
                );
                const showDailyCounter =
                  option.freeOnXynth && isFreeTier && geminiLimit > 0;
                // Free tier has no included Claude credit (grant removed
                // 2026-09-20): a paid model only runs on purchased balance.
                const needsCredits =
                  !option.freeOnXynth &&
                  isFreeTier &&
                  (usageUsd.bonus_balance_usd ?? 0) <= 0;
                return (
                  <button
                    key={option.key}
                    type="button"
                    role="radio"
                    aria-checked={isActive}
                    aria-label={option.label}
                    onClick={() => {
                      if (!isActive) applyModelSelection(option.key);
                      setModelPickerOpen(false);
                    }}
                    className={cx(
                      "group w-full rounded-xl border p-3 text-left transition-all",
                      "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring/60",
                      isActive
                        ? "border-white/20 bg-white/[0.05]"
                        : "border-white/[0.06] bg-white/[0.02] hover:border-white/15 hover:bg-white/[0.04]"
                    )}
                  >
                    <div className="flex items-center gap-2.5">
                      <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-white/5 ring-1 ring-white/10">
                        <Image
                          src={option.iconSrc}
                          alt=""
                          width={18}
                          height={18}
                          className="shrink-0"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <span className="truncate text-[13.5px] font-semibold text-white">
                            {option.label}
                          </span>
                          {option.isNew && (
                            <span className="rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[8.5px] font-bold uppercase tracking-wider text-emerald-300 ring-1 ring-emerald-400/30">
                              New
                            </span>
                          )}
                          {option.recommended && (
                            <span className="rounded-full bg-white/10 px-1.5 py-0.5 text-[8.5px] font-bold uppercase tracking-wider text-white ring-1 ring-white/20">
                              Best
                            </span>
                          )}
                        </div>
                        <p className="mt-0.5 text-[10.5px] text-white/70">
                          {option.time} per task
                        </p>
                      </div>
                      {isActive && (
                        <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/20 ring-1 ring-inset ring-emerald-400/30">
                          <Check className="size-3 text-emerald-300" />
                        </span>
                      )}
                    </div>

                    <div className="mt-2.5 flex items-center justify-between rounded-lg bg-black/20 px-2.5 py-2 ring-1 ring-white/[0.06]">
                      {option.freeOnXynth ? (
                        <>
                          <span className="text-[11.5px] font-semibold text-emerald-300">
                            Free on every plan
                          </span>
                          <span className="text-[10.5px] text-white/70 line-through">
                            ${option.pricing.in.toFixed(2)} in · $
                            {option.pricing.out.toFixed(2)} out
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="text-[11.5px] font-semibold text-white">
                            ${option.pricing.in.toFixed(2)} in · $
                            {option.pricing.out.toFixed(2)} out
                          </span>
                          <span className="text-[9.5px] font-medium uppercase tracking-wide text-white/70">
                            {needsCredits ? "needs credits" : "per 1M tokens"}
                          </span>
                        </>
                      )}
                    </div>

                    <ul className="mt-2.5 flex flex-col gap-1">
                      {option.bullets.map((b) => (
                        <li
                          key={b}
                          className="flex items-start gap-2 text-[11.5px] leading-snug text-white/80"
                        >
                          <span className="mt-[6px] size-[3px] shrink-0 rounded-full bg-white/40" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>

                    {showDailyCounter && (
                      <div className="mt-2.5 rounded-lg bg-emerald-500/[0.06] px-2.5 py-2 ring-1 ring-emerald-400/20">
                        <div className="flex items-center justify-between text-[10.5px] font-medium">
                          <span className="text-emerald-300">
                            Free plan daily limit
                          </span>
                          <span className="text-white">
                            {geminiRemaining} of {geminiLimit} left today
                          </span>
                        </div>
                        <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-black/40">
                          <div
                            className="h-full rounded-full bg-emerald-400/80 transition-all"
                            style={{
                              width: `${Math.min(100, (geminiRemaining / geminiLimit) * 100)}%`,
                            }}
                          />
                        </div>
                      </div>
                    )}

                    {option.caution && (
                      <div className="mt-2.5 flex items-start gap-1.5 text-[10.5px] leading-snug text-amber-200/90">
                        <AlertTriangle className="mt-[1px] size-3 shrink-0 opacity-70" />
                        <span>{option.caution}</span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {hasMidConvoMessages && (
            <div className="border-t border-amber-500/20 bg-amber-500/10 px-4 py-2.5 text-center text-[10.5px] leading-snug tracking-tight text-amber-200/90">
              Switching mid conversation increases usage.
            </div>
          )}
        </DialogContent>
      </Dialog>
    );
  };

  // Typewriter effect - only run when there are no messages.
  // Applies to ALL variants (including workflow / replay) so the rotating
  // "Hey Xynth, give me a breakdown of nvda…" stops as soon as the user
  // (or the replay) has sent the first message.
  //
  // Opt-out: if the caller passes a single-element placeholderTexts array
  // we render that text statically with no typing animation. DemoReplayInput
  // uses this so the bottom input bar during a recorded replay doesn't
  // distract the viewer with rotating sample prompts — the attention should
  // be on the replay, not on the input bar.
  useEffect(() => {
    if (effectiveMessages.length > 0) {
      setCurrentText("");
      return;
    }

    if (texts.length <= 1) {
      setCurrentText(texts[0] ?? "");
      return;
    }

    const currentFullText = texts[currentTextIndex];

    const timeout = setTimeout(
      () => {
        if (!isDeleting && currentText.length < currentFullText.length) {
          // Typing
          setCurrentText(currentFullText.slice(0, currentText.length + 1));
        } else if (
          !isDeleting &&
          currentText.length === currentFullText.length
        ) {
          // Pause before deleting
          setTimeout(() => setIsDeleting(true), 2000);
        } else if (isDeleting && currentText.length > 0) {
          // Deleting
          setCurrentText(currentText.slice(0, -1));
        } else if (isDeleting && currentText.length === 0) {
          // Move to next text
          setIsDeleting(false);
          setCurrentTextIndex((prev) => (prev + 1) % texts.length);
        }
      },
      isDeleting ? 20 : 40
    );

    return () => clearTimeout(timeout);
  }, [
    currentText,
    currentTextIndex,
    isDeleting,
    effectiveMessages,
    user,
    isWorkflow,
    texts,
  ]);

  useEffect(() => {
    if (textareaRef.current) {
      // On mount, if there's no draft text there's nothing to measure — let
      // the CSS `min-h-[42px]` give us the single-line rest height. Calling
      // adjustHeight() with no content while the container is mid-slide-in
      // (e.g. the chat column animating 0→384px when opened on the alert
      // page) made scrollHeight return a stale/constrained value, which
      // pinned the textarea to ~160px on first paint. Only auto-grow when
      // the user actually has content.
      if ((textareaRef.current.value || "").length > 0) {
        adjustHeight();
      } else {
        resetHeight();
      }
    }
  }, []);

  const adjustHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${
        textareaRef.current.scrollHeight + 2
      }px`;
    }
  };

  const resetHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "";
    }
  };

  // Demo cards now navigate to clean demo URLs; no inline submit handler needed

  // Recompute height when layout changes (viewport width),
  // so the inline pixel height stays in sync with wrapping. Same guard as
  // the mount effect — no content, no measurement.
  useEffect(() => {
    if (textareaRef.current) {
      if ((textareaRef.current.value || "").length > 0) {
        adjustHeight();
      } else {
        resetHeight();
      }
    }
  }, [width]);

  // Cross-component prefill listener. The alerts-onboarding modal (and
  // any future caller) fires `xynth:prefill-input` with { text } in the
  // detail. Because the textarea is wired to local `draft` state instead
  // of useChat's `input`, we listen HERE and call setDraft directly so
  // the value actually lands in the visible textarea. Also mirrors to
  // localStorage so the draft persists a refresh, and focuses the
  // textarea so the cursor sits at the end ready to type.
  useEffect(() => {
    replayLog("mmi:prefill-listener-attached", { chatId });
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ text?: string }>).detail;
      replayLog("mmi:prefill-event-received", {
        chatId,
        hasText: Boolean(detail?.text),
        textLen: detail?.text?.length ?? 0,
        textPreview: (detail?.text ?? "").slice(0, 80),
      });
      // Allow empty-string payloads as an explicit clear. The replay
      // continuation flow dispatches `{ text: "" }` after the viewer
      // clicks Send so the textarea visibly empties even on same-tick
      // re-render races. Treat `text === undefined` as a no-op (legacy
      // callers); `""` is a deliberate clear.
      if (detail?.text === undefined || detail?.text === null) return;
      setDraft(detail.text);
      try {
        if (detail.text) {
          localStorage.setItem("xynth-draft-input", detail.text);
        } else {
          localStorage.removeItem("xynth-draft-input");
        }
      } catch {}
      replayLog("mmi:prefill-setdraft-called", {
        chatId,
        textLen: detail.text.length,
      });
      if (detail.text === "") {
        // Clear path: snap textarea height back to its single-line
        // default so the bar doesn't stay tall after a multi-line
        // prefill is wiped, and skip the focus / shake animations
        // that only make sense when introducing new content.
        resetHeight();
        return;
      }
      // Defer focus + cursor placement to the next tick so React has
      // re-rendered the textarea with the new value first.
      window.setTimeout(() => {
        const ta = textareaRef.current;
        replayLog("mmi:prefill-focus-tick", {
          chatId,
          hasTextareaRef: Boolean(ta),
          taValueLen: ta?.value?.length ?? -1,
        });
        if (!ta) return;
        ta.focus();
        try {
          ta.setSelectionRange(ta.value.length, ta.value.length);
        } catch {}
        adjustHeight();
        // Scroll the textarea into view so the user sees the prefilled
        // prompt land in the chatbox after the modal closes.
        try {
          ta.scrollIntoView({ behavior: "smooth", block: "center" });
        } catch {}
        // Subtle shake to grab attention right after the text lands.
        // Targets the composer container (one level up from the textarea)
        // so we jiggle the whole bubble, not just the text field. Uses
        // the Web Animations API so no CSS file edits needed.
        try {
          const target =
            (ta.closest("form") as HTMLElement | null) ??
            (ta.parentElement as HTMLElement | null) ??
            ta;
          target.animate?.(
            [
              { transform: "translateX(0)" },
              { transform: "translateX(-5px)" },
              { transform: "translateX(5px)" },
              { transform: "translateX(-3px)" },
              { transform: "translateX(3px)" },
              { transform: "translateX(0)" },
            ],
            { duration: 380, easing: "ease-out" },
          );
        } catch {}
      }, 80);
    };
    window.addEventListener("xynth:prefill-input", handler);
    return () => window.removeEventListener("xynth:prefill-input", handler);
  }, []);

  const handleInput = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.target.value;
    setDraft(value);
    try { localStorage.setItem("xynth-draft-input", value); } catch {}
    adjustHeight();
  };

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadQueue, setUploadQueue] = useState<Array<Attachment>>([]);
  const [isDragOver, setIsDragOver] = useState(false);

  const stopMicrophoneStream = useCallback(() => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      const recorder = mediaRecorderRef.current;
      if (recorder && recorder.state !== "inactive") {
        recorder.onstop = null;
        recorder.stop();
      }
      stopMicrophoneStream();
    };
  }, [stopMicrophoneStream]);

  const transcribeAudioBlob = async (audioBlob: Blob) => {
    try {
      const formData = new FormData();
      const extension = audioBlob.type.includes("mp4") ? "m4a" : "webm";
      formData.append("file", audioBlob, `recording.${extension}`);

      const response = await fetch(`${API_BASE_URL}/upload/transcribe`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Transcription failed");
      }

      const data = (await response.json()) as { text?: string };
      const transcript = (data.text || "").trim();

      if (!transcript) {
        toast.error("No speech detected. Please try again.");
        return;
      }

      setDraft((prevDraft) => {
        const prefix = prevDraft.trim().length > 0 ? `${prevDraft.trimEnd()} ` : "";
        const nextDraft = `${prefix}${transcript}`;
        try {
          localStorage.setItem("xynth-draft-input", nextDraft);
        } catch {}
        return nextDraft;
      });

      requestAnimationFrame(() => adjustHeight());
    } catch (error) {
      toast.error("Failed to transcribe audio. Please try again.");
    } finally {
      setIsTranscribingAudio(false);
    }
  };

  const startAudioRecording = useCallback(async () => {
    if (effectiveStatus !== "ready" || isRerunning || isInitializing || isTranscribingAudio) {
      return;
    }

    if (
      typeof window === "undefined" ||
      !("MediaRecorder" in window) ||
      !navigator.mediaDevices?.getUserMedia
    ) {
      toast.error("Voice recording is not supported in this browser.");
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      audioChunksRef.current = [];
      discardRecordingRef.current = false;

      const recorder = new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      recorder.onerror = () => {
        toast.error("Recording failed. Please try again.");
        setIsRecordingAudio(false);
        setIsTranscribingAudio(false);
        stopMicrophoneStream();
      };

      recorder.onstop = async () => {
        const shouldDiscard = discardRecordingRef.current;
        discardRecordingRef.current = false;

        const chunks = audioChunksRef.current;
        audioChunksRef.current = [];

        mediaRecorderRef.current = null;
        setIsRecordingAudio(false);
        stopMicrophoneStream();

        if (shouldDiscard) {
          setIsTranscribingAudio(false);
          return;
        }

        if (chunks.length === 0) {
          setIsTranscribingAudio(false);
          toast.error("No audio captured. Please try again.");
          return;
        }

        const audioBlob = new Blob(chunks, {
          type: recorder.mimeType || "audio/webm",
        });

        await transcribeAudioBlob(audioBlob);
      };

      recorder.start();
      setIsRecordingAudio(true);
    } catch (error) {
      stopMicrophoneStream();
      setIsRecordingAudio(false);
      toast.error("Microphone permission denied or unavailable.");
    }
  }, [effectiveStatus, isInitializing, isRerunning, isTranscribingAudio, stopMicrophoneStream]);

  const cancelAudioRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === "inactive") {
      return;
    }

    discardRecordingRef.current = true;
    setIsTranscribingAudio(false);
    recorder.stop();
  }, []);

  const finishAudioRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === "inactive") {
      return;
    }

    discardRecordingRef.current = false;
    setIsTranscribingAudio(true);
    recorder.stop();
  }, []);

  const submitForm = useCallback(() => {
    if (!draft.trim()) {
      telemetry.emit("chat.input_blocked", {
        session_id: chatId,
        user_id: user?.uid,
        reason: activeSkills.length ? "empty_draft_skill" : "empty_draft",
      });
      if (activeSkills.length) {
        // Mandatory input for a skill run: never launch a skill with no input.
        // eslint-disable-next-line no-console
        console.log("[SKILL-LAUNCH] blocked send: skill(s) active but input empty", { skills: activeSkills });
        setShowSkillNudge(true);
        toast.error(
          activeSkills.length === 1
            ? `Add your input for ${activeSkills[0].name} before sending.`
            : `Add your input before sending (${activeSkills.length} skills armed).`,
        );
      } else {
        toast.error("Enter a message before sending.");
      }
      return;
    }

    // Block submission if rerunning or initializing
    if (isRerunning || isInitializing) {
      telemetry.emit("chat.input_blocked", {
        session_id: chatId,
        user_id: user?.uid,
        reason: isRerunning ? "rerunning" : "initializing",
      });
      toast.error("Please wait for the re-run to complete");
      return;
    }

    if (isRecordingAudio) {
      telemetry.emit("chat.input_blocked", {
        session_id: chatId,
        user_id: user?.uid,
        reason: "recording_audio",
      });
      toast.error("Finish recording or cancel it before sending.");
      return;
    }

    if (isTranscribingAudio) {
      telemetry.emit("chat.input_blocked", {
        session_id: chatId,
        user_id: user?.uid,
        reason: "transcribing_audio",
      });
      toast.error("Please wait for transcription to finish.");
      return;
    }

    // Workflow mode: use standalone submit
    if (isWorkflow && onStandaloneSubmit) {
      telemetry.emit("chat.input_submit", {
        session_id: chatId,
        user_id: user?.uid,
        mode: "workflow",
        draft_len: draft.trim().length,
        attachment_count: attachments.length,
      });
      onStandaloneSubmit(draft, attachments);
      setDraft("");
      setAttachments([]);
      resetHeight();
      return;
    }

    // Let the backend handle balance enforcement — it returns 403 which
    // triggers the error card with Add Funds + Upgrade Plan options.
    const isTrialUser = usageData?.free_trial?.active;
    const isFreeUser = usageData?.subscription?.tier === "free";

    // Low balance warning: suggest switching to Fast mode to save money
    if (!(isFreeUser && !isTrialUser)) {
      const availableUsd = usageUsd.total_available_usd;
      if (availableUsd > 0 && availableUsd <= 10 && modelTier === "max") {
        toast.warning("Low balance. Switch to Gemini to stretch your remaining usage.", { duration: 5000 });
      }
    }

    // Sync deep research ref before submission
    if (deepResearchRef) {
      deepResearchRef.current = deepResearch;
    }
    // Sync model tier ref before submission
    const effectiveTierForSubmit = deepResearch ? "max" : modelTier;
    if (modelTierRef) {
      modelTierRef.current = effectiveTierForSubmit;
    }
    // Sync the specific-model override slug before submission. Deep Research
    // forces the most powerful model, so clear any override and let the
    // backend pick the tier default (Opus).
    if (modelOverrideRef) {
      modelOverrideRef.current = deepResearch
        ? undefined
        : modelByKey(selectedModelKey).slug;
    }
    debugLog("stream-submit", {
      chatId, deepResearch, modelTier, effectiveTier: effectiveTierForSubmit,
      draftLen: draft.trim().length,
      availableUsd: usageUsd.total_available_usd,
      activeBudgetUsd: usageUsd.active_budget_usd,
      activeRemainingUsd: usageUsd.active_remaining_usd,
    });
    telemetry.emit("chat.input_submit", {
      session_id: chatId,
      user_id: user?.uid,
      mode: "chat",
      authenticated: Boolean(user),
      draft_len: draft.trim().length,
      attachment_count: attachments.length,
      deep_research: deepResearch,
      model_tier: effectiveTierForSubmit,
      editing_cell_index: editingCell?.index,
      available_usd: usageUsd.total_available_usd,
      active_budget_usd: usageUsd.active_budget_usd,
      active_remaining_usd: usageUsd.active_remaining_usd,
    });

    // Check if user is authenticated
    if (!user) {
      telemetry.emit("chat.input_blocked", {
        session_id: chatId,
        reason: "unauthenticated",
        draft_len: draft.trim().length,
        attachment_count: attachments.length,
      });
      if (width && width > 768) {
        setShowAuthModal(true);
      } else {
        router.push("/signup");
      }
      return;
    }

    // Default: rewrite the URL to /chat/{id} so a reload lands on the chat
    // (needed when submitting from the homepage `/`). BUT: on the alert page
    // (/alerts/[id]) we MUST NOT overwrite the URL — the alert session is a
    // dedicated editor, not a generic chat, and the user needs the `/alerts/`
    // path to stay so the AlertCanvas + AlertTopBar keep rendering.
    if (
      typeof window !== "undefined" &&
      !window.location.pathname.startsWith("/alerts/")
    ) {
      window.history.replaceState({}, "", `/chat/${chatId}`);
    }

    // Prepend cell information if editing a cell
    const messageToSend = editingCell
      ? `CELL ${editingCell.index}: ${
          editingCell.purpose.length > 50
            ? editingCell.purpose.substring(0, 25) + "..."
            : editingCell.purpose
        }\n\n\n${draft}`
      : draft;

    // Skill launch (no-blob): tag THIS message with the active skill id(s) so
    // <Chat> sends `skill_ids` (backend resolves + injects the instructions).
    // The visible bubble stays the user's own input.
    if (activeSkills.length && activeSkillIdRef) {
      activeSkillIdRef.current = activeSkills.map((s) => s.id);
      // eslint-disable-next-line no-console
      console.log("[SKILL-LAUNCH] submit with active skills", { skillIds: activeSkills.map((s) => s.id), inputLen: draft.trim().length });
      telemetry.emit("skill.launch_submit", {
        session_id: chatId,
        user_id: user?.uid,
        skill_id: activeSkills[0]?.id,
        skill_ids: activeSkills.map((s) => s.id),
        input_len: draft.trim().length,
      });
    }

    // Send message with append (cleaner than manipulating input state)
    if (append) {
      append(
        {
          role: "user",
          content: messageToSend,
        },
        {
          experimental_attachments: attachments,
        }
      );
    }

    // One-shot: clear the active skills after they've been sent.
    if (activeSkills.length) {
      setActiveSkills([]);
      setShowSkillNudge(false);
    }

    // Clear the draft + attachment UI after submission
    setDraft("");
    try { localStorage.removeItem("xynth-draft-input"); } catch {}
    setAttachments([]);
    resetHeight();
    requestAnimationFrame(() => adjustHeight());
    // Clear chips visually; backend ref is cleared in Chat.experimental_prepareRequestBody
    setSelectedData([]);
    // Clear editing cell
    if (onClearEditingCell) {
      onClearEditingCell();
    }
    if (width && width > 768) {
      textareaRef.current?.focus();
    }
  }, [
    append,
    attachments,
    setAttachments,
    width,
    chatId,
    user,
    router,
    isRerunning,
    isInitializing,
    onClearEditingCell,
    editingCell,
    draft,
    activeSkills,
    activeSkillIdRef,
    isWorkflow,
    onStandaloneSubmit,
    deepResearch,
    deepResearchRef,
    isRecordingAudio,
    isTranscribingAudio,
    modelTier,
    modelTierRef,
    usageData?.free_trial?.active,
    usageData?.subscription?.tier,
    usageUsd.source,
    usageUsd.total_available_usd,
    usageUsd.active_budget_usd,
    usageUsd.active_remaining_usd,
  ]);

  const uploadFile = async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await fetch(`${API_BASE_URL}/upload/file`, {
        method: "POST",
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        const { url, pathname, contentType } = data;

        return {
          url,
          name: pathname,
          contentType: contentType,
        };
      }
      const { error } = await response.json();
      toast.error(error);
    } catch (error) {
      toast.error("Failed to upload file, please try again!");
    }
  };

  // Minimal addition: ingest document upload for PDF/TXT/DOCX
  const uploadIngest = async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await fetch(`${API_BASE_URL}/upload/ingest`, {
        method: "POST",
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        const { docId } = data;

        return {
          url: docId, // store docId in url per agreed contract
          name: file.name,
          contentType: file.type || "application/octet-stream",
        } as const;
      }
      const { error } = await response.json();
      toast.error(error);
    } catch (error) {
      toast.error("Failed to ingest document, please try again!");
    }
  };

  const handleFileChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files || []);

      // Validate file types - allow images, CSV/JSON/Excel, and documents (PDF/TXT/DOCX)
      const allowedImageTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/webp",
      ];
      const allowedDocTypes = [
        "application/pdf",
        "text/plain",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];

      const validFiles = files.filter((file) => {
        const isValidImage = allowedImageTypes.includes(file.type);
        const lower = file.name.toLowerCase();
        const isValidCSV = lower.endsWith(".csv") || file.type === "text/csv";
        const isValidJSON =
          lower.endsWith(".json") || file.type === "application/json";
        const isValidExcel =
          lower.endsWith(".xlsx") ||
          lower.endsWith(".xls") ||
          file.type ===
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
          file.type === "application/vnd.ms-excel";
        const isValidDoc =
          allowedDocTypes.includes(file.type) ||
          lower.endsWith(".pdf") ||
          lower.endsWith(".txt") ||
          lower.endsWith(".docx");
        const isValidType =
          isValidImage ||
          isValidCSV ||
          isValidJSON ||
          isValidExcel ||
          isValidDoc;

        if (!isValidType) {
          toast.error(
            `File "${file.name}" is not supported. Only JPEG, PNG, GIF, WebP images and .csv, .json, .xlsx, .xls, .pdf, .txt, .docx files are allowed.`
          );
        }
        return isValidType;
      });

      if (validFiles.length === 0) return;

      // Create lightweight local previews while uploading
      setUploadQueue(
        validFiles.map((file) => ({
          url: URL.createObjectURL(file),
          name: file.name,
          contentType: file.type || "application/octet-stream",
        }))
      );

      try {
        const uploadPromises = validFiles.map((file) => {
          const lower = file.name.toLowerCase();
          const isDoc =
            allowedDocTypes.includes(file.type) ||
            lower.endsWith(".pdf") ||
            lower.endsWith(".txt") ||
            lower.endsWith(".docx");
          return isDoc ? uploadIngest(file) : uploadFile(file);
        });
        const uploadedAttachments = await Promise.all(uploadPromises);
        const successfullyUploadedAttachments = uploadedAttachments.filter(
          (attachment) => attachment !== undefined
        );

        setAttachments((currentAttachments) => [
          ...currentAttachments,
          ...successfullyUploadedAttachments,
        ]);
      } catch (error) {
      } finally {
        setUploadQueue((prev) => {
          prev.forEach((a) => {
            if (a.url && a.url.startsWith("blob:")) {
              URL.revokeObjectURL(a.url);
            }
          });
          return [];
        });
      }
    },
    [setAttachments]
  );

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    // Only set drag over to false if we're leaving the main container
    if (!event.currentTarget.contains(event.relatedTarget as Node)) {
      setIsDragOver(false);
    }
  }, []);

  const handleDrop = useCallback(
    async (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragOver(false);

      const files = Array.from(event.dataTransfer.files);
      if (files.length === 0) return;

      // Filter for supported file types (images, CSV/JSON/Excel, and documents PDF/TXT/DOCX)
      const allowedImageTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/webp",
      ];
      const allowedDocTypes = [
        "application/pdf",
        "text/plain",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];

      const supportedFiles = files.filter((file) => {
        const isValidImage = allowedImageTypes.includes(file.type);
        const lower = file.name.toLowerCase();
        const isValidCSV = lower.endsWith(".csv") || file.type === "text/csv";
        const isValidJSON =
          lower.endsWith(".json") || file.type === "application/json";
        const isValidExcel =
          lower.endsWith(".xlsx") ||
          lower.endsWith(".xls") ||
          file.type ===
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
          file.type === "application/vnd.ms-excel";
        const isValidDoc =
          allowedDocTypes.includes(file.type) ||
          lower.endsWith(".pdf") ||
          lower.endsWith(".txt") ||
          lower.endsWith(".docx");
        const isValidType =
          isValidImage ||
          isValidCSV ||
          isValidJSON ||
          isValidExcel ||
          isValidDoc;

        if (!isValidType) {
          toast.error(
            `File "${file.name}" is not supported. Only JPEG, PNG, GIF, WebP images and .csv, .json, .xlsx, .xls, .pdf, .txt, .docx files are allowed.`
          );
        }
        return isValidType;
      });

      if (supportedFiles.length === 0) {
        return;
      }

      // Use the existing upload logic (route docs to ingest)
      // Create lightweight local previews while uploading
      setUploadQueue(
        supportedFiles.map((file) => ({
          url: URL.createObjectURL(file),
          name: file.name,
          contentType: file.type || "application/octet-stream",
        }))
      );

      try {
        const uploadPromises = supportedFiles.map((file) => {
          const lower = file.name.toLowerCase();
          const isDoc =
            allowedDocTypes.includes(file.type) ||
            lower.endsWith(".pdf") ||
            lower.endsWith(".txt") ||
            lower.endsWith(".docx");
          return isDoc ? uploadIngest(file) : uploadFile(file);
        });
        const uploadedAttachments = await Promise.all(uploadPromises);
        const successfullyUploadedAttachments = uploadedAttachments.filter(
          (attachment) => attachment !== undefined
        );

        setAttachments((currentAttachments) => [
          ...currentAttachments,
          ...successfullyUploadedAttachments,
        ]);
      } catch (error) {
      } finally {
        setUploadQueue((prev) => {
          prev.forEach((a) => {
            if (a.url && a.url.startsWith("blob:")) {
              URL.revokeObjectURL(a.url);
            }
          });
          return [];
        });
      }
    },
    [setAttachments, uploadFile, uploadIngest]
  );

  const handlePaste = useCallback(
    async (event: React.ClipboardEvent) => {
      const items = event.clipboardData?.items;
      if (!items) return;

      const allowedImageTypes = [
        "image/jpeg",
        "image/png",
        "image/gif",
        "image/webp",
      ];
      const allowedDocTypes = [
        "application/pdf",
        "text/plain",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];

      const pastedFiles: File[] = [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind !== "file") continue;
        const file = item.getAsFile();
        if (!file) continue;

        const lower = file.name.toLowerCase();
        const isImage = allowedImageTypes.includes(file.type);
        const isDoc =
          allowedDocTypes.includes(file.type) ||
          lower.endsWith(".pdf") ||
          lower.endsWith(".txt") ||
          lower.endsWith(".docx");
        const isCSV = lower.endsWith(".csv") || file.type === "text/csv";
        const isJSON = lower.endsWith(".json") || file.type === "application/json";
        const isExcel =
          lower.endsWith(".xlsx") ||
          lower.endsWith(".xls") ||
          file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
          file.type === "application/vnd.ms-excel";

        if (isImage || isDoc || isCSV || isJSON || isExcel) {
          pastedFiles.push(file);
        }
      }

      if (pastedFiles.length === 0) return;

      event.preventDefault();

      setUploadQueue(
        pastedFiles.map((file) => ({
          url: URL.createObjectURL(file),
          name: file.name || `pasted-file.${file.type.split("/")[1] || "bin"}`,
          contentType: file.type || "application/octet-stream",
        }))
      );

      try {
        const uploadPromises = pastedFiles.map((file) => {
          const lower = file.name.toLowerCase();
          const isDoc =
            allowedDocTypes.includes(file.type) ||
            lower.endsWith(".pdf") ||
            lower.endsWith(".txt") ||
            lower.endsWith(".docx");
          return isDoc ? uploadIngest(file) : uploadFile(file);
        });
        const uploaded = await Promise.all(uploadPromises);
        const successful = uploaded.filter(
          (a) => a !== undefined
        ) as Attachment[];
        setAttachments((curr) => [...curr, ...successful]);
      } catch {
        // Errors already handled by uploadFile/uploadIngest toast
      } finally {
        setUploadQueue((prev) => {
          prev.forEach((a) => {
            if (a.url?.startsWith("blob:")) URL.revokeObjectURL(a.url);
          });
          return [];
        });
      }
    },
    [setAttachments, uploadFile, uploadIngest]
  );

  const { isAtBottom, scrollToBottom } = useScrollToBottom();
  const { artifact } = useArtifact();

  useEffect(() => {
    if (effectiveStatus === "streaming") {
      scrollToBottom("smooth");
    }
  }, [effectiveStatus, scrollToBottom]);

  // Conditionally render as motion.div when no messages, regular div otherwise
  const commonProps = {
    // data-testid on the outer wrapper lets the buddy-tour overlay
    // (a) spotlight the whole input bar as a single anchor and
    // (b) clamp other beats so their spotlight doesn't bleed into
    // this region. Stable across both render paths (workflow / chat)
    // because commonProps is shared.
    "data-testid": "multimodal-input-bar",
    className: cx(
      "relative w-full flex flex-col gap-4",
      isDragOver &&
        "bg-muted/50 rounded-lg border-2 border-dashed border-muted-foreground/30"
    ),
    onDragOver: handleDragOver,
    onDragLeave: handleDragLeave,
    onDrop: handleDrop,
  };

  // For workflow variant, always use the simpler layout (like messages.length === 0)
  const showSimpleLayout = isWorkflow || effectiveMessages.length === 0;

  const content = showSimpleLayout ? (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 10 }}
      transition={{ delay: 0.7 }}
      {...commonProps}
    >
      {/* Content will be the same for both */}
      {isDragOver && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-muted/80 rounded-lg border-2 border-dashed border-muted-foreground/30">
          <div className="text-center">
            <div className="text-foreground text-lg font-medium">
              Drop files here to upload
            </div>
            <div className="text-muted-foreground text-sm mt-1">
              Supports images and documents
            </div>
          </div>
        </div>
      )}
      <AnimatePresence>
        {!isAtBottom && effectiveMessages.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
            className="absolute left-[calc(50%-20px)] bottom-28 -translate-x-1/2 z-50"
          >
            <Button
              data-testid="scroll-to-bottom-button"
              className="rounded-full"
              size="icon"
              variant="outline"
              onClick={(event) => {
                event.preventDefault();
                scrollToBottom();
              }}
            >
              <ArrowDown />
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      <input
        type="file"
        className="fixed -top-4 -left-4 size-0.5 opacity-0 pointer-events-none"
        ref={fileInputRef}
        multiple
        onChange={handleFileChange}
        tabIndex={-1}
      />

      {(attachments.length > 0 || uploadQueue.length > 0) && (
        <div
          data-testid="attachments-preview"
          className="flex flex-row gap-2 overflow-x-scroll items-end"
        >
          {attachments.map((attachment) => (
            <PreviewAttachment
              key={attachment.url}
              attachment={attachment}
              onRemove={onRemoveAttachment}
            />
          ))}

          {uploadQueue.map((att) => (
            <PreviewAttachment
              key={att.url || att.name}
              attachment={att}
              isUploading={true}
            />
          ))}
        </div>
      )}

      {selectedModelKey === "glm" &&
        (attachments.length > 0 || uploadQueue.length > 0) && (
          <div
            data-testid="glm-image-notice"
            className="mb-1.5 flex items-center gap-2 rounded-md border border-white/[0.08] bg-[#0c0c0e] px-2.5 py-1.5"
          >
            <ImageOff className="size-3 shrink-0 text-emerald-400/90" />
            <span className="text-[11px] leading-snug text-white/80">
              GLM can&apos;t see images. Switch to{" "}
              <button
                type="button"
                onClick={() => applyModelSelection("opus")}
                className="font-medium text-emerald-400 underline-offset-2 hover:underline"
              >
                Opus
              </button>{" "}
              to include this screenshot.
            </span>
          </div>
        )}

      <div
        data-testid="multimodal-input-surface"
        className={cx(
          "relative overflow-hidden bg-muted transition-all duration-200",
          effectiveMessages.length === 0
            ? "rounded-[22px] px-4 pt-3 pb-3"
            : "rounded-2xl px-3 pt-2.5 pb-2.5",
          (isRerunning || isInitializing) && "border-2 border-blue-500/50"
        )}
      >
        {(isRerunning || isInitializing) && (
          <div className="absolute inset-0 bg-blue-500/5 pointer-events-none" />
        )}

        {(isRerunning || isInitializing) && (
          <div className="flex items-center gap-1 text-sm text-blue-400 font-medium mb-1 px-1">
            <span>{isInitializing ? "Initializing" : "Running"}</span>
            <span className="inline-flex">
              <span className="animate-[pulse_1.4s_ease-in-out_infinite]">.</span>
              <span className="animate-[pulse_1.4s_ease-in-out_0.2s_infinite]">.</span>
              <span className="animate-[pulse_1.4s_ease-in-out_0.4s_infinite]">.</span>
            </span>
          </div>
        )}

        {promptPills && promptPills.length > 0 && (
          <div className="-mx-0.5 mb-1.5 overflow-x-auto overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <div className="flex w-max items-center gap-1.5 px-0.5">
              {promptPills.map((prompt) => (
                <button
                  key={prompt}
                  type="button"
                  onClick={() => onPromptPillClick?.(prompt)}
                  className={[
                    "h-6 px-2.5 rounded-md shrink-0",
                    "border border-white/10 bg-transparent",
                    "text-[11px] font-medium text-slate-100/85",
                    "hover:bg-white/[0.04] hover:border-white/20 hover:text-slate-50",
                    "transition-colors",
                  ].join(" ")}
                >
                  {prompt}
                </button>
              ))}
            </div>
          </div>
        )}

        {editingCell && (
          <div className="flex items-center gap-2 px-2 py-1.5 mb-2 bg-secondary/50 rounded-lg border border-border">
            <Badge variant="outline" className="text-xs font-mono shrink-0">
              {editingCell.index}
            </Badge>
            <span className="text-xs truncate max-w-[200px] text-muted-foreground">
              {editingCell.purpose}
            </span>
            <Button
              size="sm"
              variant="ghost"
              className="h-5 w-5 p-0 ml-auto hover:bg-muted shrink-0"
              onClick={onClearEditingCell}
              title="Clear editing cell"
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        )}

        {/* Skill launch (no-blob): active skill chips + brief input nudge. */}
        {activeSkills.length > 0 && (
          <div className="-mx-0.5 mb-2 overflow-x-auto overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <div className="flex w-max min-w-full items-center gap-1.5 px-0.5">
              {activeSkills.map((s) => (
                <span
                  key={s.id}
                  className="inline-flex h-7 max-w-[52vw] shrink-0 items-center gap-1.5 rounded-md border border-emerald-400/25 bg-emerald-400/[0.08] px-2 text-[11px] font-semibold text-white shadow-[0_0_18px_rgba(16,185,129,0.08)] sm:max-w-[220px]"
                >
                  <Sparkles className="size-3.5 shrink-0 text-emerald-300" />
                  <span className="truncate">{s.name}</span>
                  <button
                    type="button"
                    aria-label="Remove skill"
                    onClick={() => {
                      // eslint-disable-next-line no-console
                      console.log("[SKILL-LAUNCH] cleared active skill via chip", s);
                      setActiveSkills((prev) => {
                        const next = prev.filter((p) => p.id !== s.id);
                        if (!next.length) setShowSkillNudge(false);
                        return next;
                      });
                    }}
                    className="ml-0.5 shrink-0 rounded p-0.5 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
                  >
                    <X className="size-3" />
                  </button>
                </span>
              ))}
              {showSkillNudge && (
                <span className="shrink-0 text-[11px] font-medium text-white/80">
                  Add input, then send.
                </span>
              )}
            </div>
          </div>
        )}

        <Textarea
          data-testid="multimodal-input"
          ref={textareaRef}
          placeholder={
            isRecordingAudio
              ? "Recording..."
              : isTranscribingAudio
              ? "Transcribing audio..."
              : isInitializing || isRerunning
              ? ""
              : effectiveMessages.length > 0
              ? "Ask a follow up..."
              : draft
              ? "Send a message..."
              : texts.length <= 1
              ? "Ask a follow up..."
              : `Hey Xynth, ${currentText}`
          }
          value={draft}
          onChange={handleInput}
          disabled={isRerunning || isInitializing || isRecordingAudio || isTranscribingAudio}
          readOnly={preventFocus || forceReadOnly}
          className={cx(
            "max-h-[calc(50svh-5rem)] md:max-h-[calc(45svh-5rem)] lg:max-h-[calc(36rem-5rem)] overflow-y-auto overscroll-contain resize-none !bg-transparent !border-none !p-0 !focus:outline-none !focus:ring-0 !focus:border-none !focus:shadow-none focus-visible:outline-none focus-visible:ring-0",
            effectiveMessages.length === 0
              ? "min-h-[42px] !text-[16px] md:!text-[17px] !leading-[1.45] px-1"
              : "min-h-[42px] !text-[15px] px-0.5",
            (isRerunning || isInitializing || isRecordingAudio || isTranscribingAudio) &&
              "opacity-60 cursor-not-allowed",
            className
          )}
          rows={effectiveMessages.length === 0 ? 2 : 1}
          onPaste={handlePaste}
          onKeyDown={(event) => {
            // Cmd/Ctrl+Enter shortcut takes precedence over plain Enter
            // when a handler is wired. Used by the homepage composer to
            // route to the clean replay tour. Plain Enter still submits.
            if (
              event.key === "Enter" &&
              (event.metaKey || event.ctrlKey) &&
              !event.nativeEvent.isComposing &&
              onCmdEnter
            ) {
              event.preventDefault();
              const trimmedDraft = draft.trim();
              // Clear the composer like a normal submit so the textarea
              // visually empties before the navigation handler fires.
              // Also wipes the `xynth-draft-input` localStorage cache —
              // otherwise the typed text would reappear on the next
              // homepage visit / refresh.
              setDraft("");
              try {
                localStorage.removeItem("xynth-draft-input");
              } catch {}
              setAttachments([]);
              resetHeight();
              onCmdEnter(trimmedDraft);
              return;
            }
            if (
              event.key === "Enter" &&
              !event.shiftKey &&
              !event.nativeEvent.isComposing
            ) {
              event.preventDefault();

              if (isRerunning || isInitializing) {
                toast.error("Please wait for the re-run to complete");
                return;
              }

              if (isRecordingAudio) {
                toast.error("Finish recording or cancel it first.");
                return;
              }

              if (isTranscribingAudio) {
                toast.error("Please wait for transcription to finish.");
                return;
              }

              if (status !== "submitted" && status !== "streaming") {
                submitForm();
              } else {
                toast.error(
                  "Please wait for the model to finish its response!"
                );
              }
            }
          }}
        />

        {isRecordingAudio ? (
          <div className="flex items-center gap-3 pt-2 mt-1 border-t border-border/30">
            <span className="flex items-center gap-2 shrink-0">
              <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
              <span className="text-xs text-muted-foreground font-medium">Recording</span>
            </span>
            <LiveWaveform
              stream={mediaStreamRef.current}
              height={24}
              barWidth={3}
              barGap={2}
              barColor="hsl(var(--foreground))"
              className="flex-1 min-w-0"
            />
            <div className="flex items-center gap-1 shrink-0">
              <Button
                type="button"
                className="rounded-md p-[7px] h-fit hover:bg-muted"
                onClick={(e) => {
                  e.preventDefault();
                  cancelAudioRecording();
                }}
                variant="ghost"
                title="Cancel recording"
              >
                <X size={14} />
              </Button>
              <Button
                type="button"
                className="rounded-full p-1.5 h-fit border dark:border-zinc-600"
                onClick={(e) => {
                  e.preventDefault();
                  finishAudioRecording();
                }}
                title="Done recording"
              >
                <Check size={14} />
              </Button>
            </div>
          </div>
        ) : isTranscribingAudio ? (
          <div className="flex items-center justify-center gap-2 pt-2 mt-1 border-t border-border/30">
            <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Transcribing audio...</span>
          </div>
        ) : (
          <div className="pt-1.5 mt-1">
            <div className="flex items-center justify-between gap-1">
              <div className="flex items-center gap-0.5 min-w-0 overflow-x-auto">
                {!user && effectiveMessages.length > 0 && (
                  <Button
                    type="button"
                    onClick={() => {
                      router.push("/demo");
                    }}
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs bg-transparent border-none shadow-none hover:bg-muted/50 shrink-0"
                  >
                    <ArrowLeft size={13} className="mr-1" />
                    Back
                  </Button>
                )}
                {!isWorkflow && renderTierDropdown()}
                <AttachmentsButton
                  fileInputRef={fileInputRef}
                  status={effectiveStatus}
                  isRerunning={isRerunning}
                  isInitializing={isInitializing}
                  compact={artifact.isVisible || isMobile}
                />
                {!hideData && (
                  <DataSelector
                    status={effectiveStatus}
                    selected={selectedData}
                    onToggle={toggleData}
                    onRemove={removeData}
                    compact={artifact.isVisible || isMobile}
                  />
                )}
                {!isWorkflow && (
                  <SkillsButton
                    compact={artifact.isVisible || isMobile}
                    onPick={(s) => {
                      // eslint-disable-next-line no-console
                      console.log("[SKILL-LAUNCH] picked skill from chat-bar browser", s);
                      addActiveSkill({ id: s.id, name: s.name });
                      telemetry.emit("skill.picked_in_composer", {
                        session_id: chatId,
                        user_id: user?.uid,
                        skill_id: s.id,
                      });
                      if (width && width > 768) {
                        requestAnimationFrame(() => textareaRef.current?.focus());
                      }
                    }}
                  />
                )}
              </div>

              <div className="flex items-center gap-1 shrink-0">
                <VoiceRecorderButton
                  isDisabled={effectiveStatus !== "ready" || isRerunning || isInitializing}
                  onStart={startAudioRecording}
                />
                {!isWorkflow &&
                ((effectiveStatus === "submitted" ||
                  effectiveStatus === "streaming" ||
                  isAgentProcessing) &&
                setMessages) ? (
                  <StopButton
                    chatId={chatId}
                    setMessages={setMessages}
                    onStopped={onStopped}
                    onUsageRefresh={refreshUsage}
                  />
                ) : (
                  <SendButton
                    input={draft}
                    submitForm={submitForm}
                    uploadQueue={uploadQueue}
                    isRerunning={isRerunning}
                    isInitializing={isInitializing}
                    highlightSend={highlightSend}
                  />
                )}
              </div>
            </div>
            {/* Mobile-only: selected-data chips wrap onto their own row
                below the toolbar so the toolbar buttons never get pushed
                off-screen. Hidden on desktop because the inline chips
                next to the Data button already do the same job there. */}
            {isMobile && !hideData && selectedData.length > 0 && (
              <div className="mt-1.5">
                <SelectedDataChips
                  selected={selectedData}
                  onRemove={removeData}
                />
              </div>
            )}
          </div>
        )}
      </div>

      <AuthModal open={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </motion.div>
  ) : (
    <div {...commonProps}>
      {isDragOver && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-muted/80 rounded-lg border-2 border-dashed border-muted-foreground/30">
          <div className="text-center">
            <div className="text-foreground text-lg font-medium">
              Drop files here to upload
            </div>
            <div className="text-muted-foreground text-sm mt-1">
              Supports images and documents
            </div>
          </div>
        </div>
      )}
      <AnimatePresence>
        {!isAtBottom && effectiveMessages.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
            className="absolute left-[calc(50%-20px)] bottom-28 -translate-x-1/2 z-50"
          >
            <Button
              data-testid="scroll-to-bottom-button"
              className="rounded-full"
              size="icon"
              variant="outline"
              onClick={(event) => {
                event.preventDefault();
                scrollToBottom();
              }}
            >
              <ArrowDown />
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      <input
        type="file"
        className="fixed -top-4 -left-4 size-0.5 opacity-0 pointer-events-none"
        ref={fileInputRef}
        multiple
        onChange={handleFileChange}
        tabIndex={-1}
      />

      {(attachments.length > 0 || uploadQueue.length > 0) && (
        <div
          data-testid="attachments-preview"
          className="flex flex-row gap-2 overflow-x-scroll items-end"
        >
          {attachments.map((attachment) => (
            <PreviewAttachment
              key={attachment.url}
              attachment={attachment}
              onRemove={onRemoveAttachment}
            />
          ))}

          {uploadQueue.map((att) => (
            <PreviewAttachment
              key={att.url || att.name}
              attachment={att}
              isUploading={true}
            />
          ))}
        </div>
      )}

      {selectedModelKey === "glm" &&
        (attachments.length > 0 || uploadQueue.length > 0) && (
          <div
            data-testid="glm-image-notice"
            className="mb-1.5 flex items-center gap-2 rounded-md border border-white/[0.08] bg-[#0c0c0e] px-2.5 py-1.5"
          >
            <ImageOff className="size-3 shrink-0 text-emerald-400/90" />
            <span className="text-[11px] leading-snug text-white/80">
              GLM can&apos;t see images. Switch to{" "}
              <button
                type="button"
                onClick={() => applyModelSelection("opus")}
                className="font-medium text-emerald-400 underline-offset-2 hover:underline"
              >
                Opus
              </button>{" "}
              to include this screenshot.
            </span>
          </div>
        )}

      <div
        className={cx(
          "relative overflow-hidden rounded-2xl px-3 pt-2.5 pb-2.5 transition-all duration-200",
          !user
            ? "bg-black focus-within:ring-0 focus-within:outline-none"
            : "bg-muted",
          (isRerunning || isInitializing) && "border-2 border-blue-500/50"
        )}
        style={{
          boxShadow: !user
            ? "0 0 5px rgba(255, 255, 255, 0.3), 0 0 10px rgba(255, 255, 255, 0.2), 0 0 15px rgba(255, 255, 255, 0.1)"
            : undefined,
        }}
      >
        {(isRerunning || isInitializing) && (
          <div className="absolute inset-0 bg-blue-500/5 pointer-events-none" />
        )}

        {(isRerunning || isInitializing) && (
          <div className="flex items-center gap-1 text-sm text-blue-400 font-medium mb-1 px-1">
            <span>{isInitializing ? "Initializing" : "Running"}</span>
            <span className="inline-flex">
              <span className="animate-[pulse_1.4s_ease-in-out_infinite]">.</span>
              <span className="animate-[pulse_1.4s_ease-in-out_0.2s_infinite]">.</span>
              <span className="animate-[pulse_1.4s_ease-in-out_0.4s_infinite]">.</span>
            </span>
          </div>
        )}

        {promptPills && promptPills.length > 0 && (
          <div className="-mx-0.5 mb-1.5 overflow-x-auto overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <div className="flex w-max items-center gap-1.5 px-0.5">
              {promptPills.map((prompt) => (
                <button
                  key={prompt}
                  type="button"
                  onClick={() => onPromptPillClick?.(prompt)}
                  className={[
                    "h-6 px-2.5 rounded-md shrink-0",
                    "border border-white/10 bg-transparent",
                    "text-[11px] font-medium text-slate-100/85",
                    "hover:bg-white/[0.04] hover:border-white/20 hover:text-slate-50",
                    "transition-colors",
                  ].join(" ")}
                >
                  {prompt}
                </button>
              ))}
            </div>
          </div>
        )}

        {editingCell && (
          <div className="flex items-center gap-2 px-2 py-1.5 mb-2 bg-secondary/50 rounded-lg border border-border">
            <Badge variant="outline" className="text-xs font-mono shrink-0">
              {editingCell.index}
            </Badge>
            <span className="text-xs truncate max-w-[200px] text-muted-foreground">
              {editingCell.purpose}
            </span>
            <Button
              size="sm"
              variant="ghost"
              className="h-5 w-5 p-0 ml-auto hover:bg-muted shrink-0"
              onClick={onClearEditingCell}
              title="Clear editing cell"
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        )}

        {/* Skill launch (no-blob): active skill chips + brief input nudge. */}
        {activeSkills.length > 0 && (
          <div className="-mx-0.5 mb-2 overflow-x-auto overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <div className="flex w-max min-w-full items-center gap-1.5 px-0.5">
              {activeSkills.map((s) => (
                <span
                  key={s.id}
                  className="inline-flex h-7 max-w-[52vw] shrink-0 items-center gap-1.5 rounded-md border border-emerald-400/25 bg-emerald-400/[0.08] px-2 text-[11px] font-semibold text-white shadow-[0_0_18px_rgba(16,185,129,0.08)] sm:max-w-[220px]"
                >
                  <Sparkles className="size-3.5 shrink-0 text-emerald-300" />
                  <span className="truncate">{s.name}</span>
                  <button
                    type="button"
                    aria-label="Remove skill"
                    onClick={() => {
                      // eslint-disable-next-line no-console
                      console.log("[SKILL-LAUNCH] cleared active skill via chip", s);
                      setActiveSkills((prev) => {
                        const next = prev.filter((p) => p.id !== s.id);
                        if (!next.length) setShowSkillNudge(false);
                        return next;
                      });
                    }}
                    className="ml-0.5 shrink-0 rounded p-0.5 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
                  >
                    <X className="size-3" />
                  </button>
                </span>
              ))}
              {showSkillNudge && (
                <span className="shrink-0 text-[11px] font-medium text-white/80">
                  Add input, then send.
                </span>
              )}
            </div>
          </div>
        )}

        <Textarea
          data-testid="multimodal-input"
          ref={textareaRef}
          placeholder={
            isRecordingAudio
              ? "Recording..."
              : isTranscribingAudio
              ? "Transcribing audio..."
              : isInitializing || isRerunning
              ? ""
              : effectiveMessages.length > 0
              ? "Ask a follow up..."
              : draft
              ? "Send a message..."
              : texts.length <= 1
              ? "Ask a follow up..."
              : `Hey Xynth ${currentText}`
          }
          value={draft}
          onChange={handleInput}
          disabled={isRerunning || isInitializing || isRecordingAudio || isTranscribingAudio}
          readOnly={preventFocus || forceReadOnly}
          className={cx(
            "min-h-[42px] max-h-[calc(30svh-4rem)] md:max-h-[calc(28svh-4rem)] lg:max-h-[calc(24rem-4rem)] overflow-y-auto overscroll-contain resize-none !text-[15px] !bg-transparent !border-none !p-0 px-0.5 !focus:outline-none !focus:ring-0 !focus:border-none !focus:shadow-none focus-visible:outline-none focus-visible:ring-0",
            (isRerunning || isInitializing || isRecordingAudio || isTranscribingAudio) &&
              "opacity-60 cursor-not-allowed",
            className
          )}
          rows={1}
          onPaste={handlePaste}
          onKeyDown={(event) => {
            // Cmd/Ctrl+Enter shortcut takes precedence over plain Enter
            // when a handler is wired. Used by the homepage composer to
            // route to the clean replay tour. Plain Enter still submits.
            if (
              event.key === "Enter" &&
              (event.metaKey || event.ctrlKey) &&
              !event.nativeEvent.isComposing &&
              onCmdEnter
            ) {
              event.preventDefault();
              const trimmedDraft = draft.trim();
              // Clear the composer like a normal submit so the textarea
              // visually empties before the navigation handler fires.
              // Also wipes the `xynth-draft-input` localStorage cache —
              // otherwise the typed text would reappear on the next
              // homepage visit / refresh.
              setDraft("");
              try {
                localStorage.removeItem("xynth-draft-input");
              } catch {}
              setAttachments([]);
              resetHeight();
              onCmdEnter(trimmedDraft);
              return;
            }
            if (
              event.key === "Enter" &&
              !event.shiftKey &&
              !event.nativeEvent.isComposing
            ) {
              event.preventDefault();

              if (isRerunning || isInitializing) {
                toast.error("Please wait for the re-run to complete");
                return;
              }

              if (isRecordingAudio) {
                toast.error("Finish recording or cancel it first.");
                return;
              }

              if (isTranscribingAudio) {
                toast.error("Please wait for transcription to finish.");
                return;
              }

              if (status !== "submitted" && status !== "streaming") {
                submitForm();
              } else {
                toast.error(
                  "Please wait for the model to finish its response!"
                );
              }
            }
          }}
        />

        {isRecordingAudio ? (
          <div className="flex items-center gap-3 pt-2 mt-1 border-t border-border/30">
            <span className="flex items-center gap-2 shrink-0">
              <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
              <span className="text-xs text-muted-foreground font-medium">Recording</span>
            </span>
            <LiveWaveform
              stream={mediaStreamRef.current}
              height={24}
              barWidth={3}
              barGap={2}
              barColor="hsl(var(--foreground))"
              className="flex-1 min-w-0"
            />
            <div className="flex items-center gap-1 shrink-0">
              <Button
                type="button"
                className="rounded-md p-[7px] h-fit hover:bg-muted"
                onClick={(e) => {
                  e.preventDefault();
                  cancelAudioRecording();
                }}
                variant="ghost"
                title="Cancel recording"
              >
                <X size={14} />
              </Button>
              <Button
                type="button"
                className="rounded-full p-1.5 h-fit border dark:border-zinc-600"
                onClick={(e) => {
                  e.preventDefault();
                  finishAudioRecording();
                }}
                title="Done recording"
              >
                <Check size={14} />
              </Button>
            </div>
          </div>
        ) : isTranscribingAudio ? (
          <div className="flex items-center justify-center gap-2 pt-2 mt-1 border-t border-border/30">
            <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Transcribing audio...</span>
          </div>
        ) : (
          <div className="pt-1.5 mt-1">
            <div className="flex items-center justify-between gap-1">
              <div className="flex items-center gap-0.5 min-w-0 overflow-x-auto">
                {!user && effectiveMessages.length > 0 && (
                  <Button
                    type="button"
                    onClick={() => {
                      router.push("/demo");
                    }}
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs bg-transparent border-none shadow-none hover:bg-muted/50 shrink-0"
                  >
                    <ArrowLeft size={13} className="mr-1" />
                    Back
                  </Button>
                )}
                {!isWorkflow && renderTierDropdown()}
                <AttachmentsButton
                  fileInputRef={fileInputRef}
                  status={effectiveStatus}
                  isRerunning={isRerunning}
                  isInitializing={isInitializing}
                  compact={artifact.isVisible || isMobile}
                />
                {!hideData && (
                  <DataSelector
                    status={effectiveStatus}
                    selected={selectedData}
                    onToggle={toggleData}
                    onRemove={removeData}
                    compact={artifact.isVisible || isMobile}
                  />
                )}
                {!isWorkflow && (
                  <SkillsButton
                    compact={artifact.isVisible || isMobile}
                    onPick={(s) => {
                      // eslint-disable-next-line no-console
                      console.log("[SKILL-LAUNCH] picked skill from chat-bar browser", s);
                      addActiveSkill({ id: s.id, name: s.name });
                      telemetry.emit("skill.picked_in_composer", {
                        session_id: chatId,
                        user_id: user?.uid,
                        skill_id: s.id,
                      });
                      if (width && width > 768) {
                        requestAnimationFrame(() => textareaRef.current?.focus());
                      }
                    }}
                  />
                )}
              </div>

              <div className="flex items-center gap-1 shrink-0">
                <VoiceRecorderButton
                  isDisabled={effectiveStatus !== "ready" || isRerunning || isInitializing}
                  onStart={startAudioRecording}
                />
                {!isWorkflow &&
                ((effectiveStatus === "submitted" ||
                  effectiveStatus === "streaming" ||
                  isAgentProcessing) &&
                setMessages) ? (
                  <StopButton
                    chatId={chatId}
                    setMessages={setMessages}
                    onStopped={onStopped}
                    onUsageRefresh={refreshUsage}
                  />
                ) : (
                  <SendButton
                    input={draft}
                    submitForm={submitForm}
                    uploadQueue={uploadQueue}
                    isRerunning={isRerunning}
                    isInitializing={isInitializing}
                    highlightSend={highlightSend}
                  />
                )}
              </div>
            </div>
            {/* Mobile-only: selected-data chips wrap onto their own row
                below the toolbar so the toolbar buttons never get pushed
                off-screen. Hidden on desktop because the inline chips
                next to the Data button already do the same job there. */}
            {isMobile && !hideData && selectedData.length > 0 && (
              <div className="mt-1.5">
                <SelectedDataChips
                  selected={selectedData}
                  onRemove={removeData}
                />
              </div>
            )}
          </div>
        )}
      </div>

      <AuthModal open={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </div>
  );

  return content;
}

export const MultimodalInput = memo(
  PureMultimodalInput,
  (prevProps, nextProps) => {
    if (prevProps.status !== nextProps.status) return false;
    if (!equal(prevProps.attachments, nextProps.attachments)) return false;
    if (prevProps.selectedVisibilityType !== nextProps.selectedVisibilityType)
      return false;
    if (prevProps.isRerunning !== nextProps.isRerunning) return false;
    if (prevProps.isInitializing !== nextProps.isInitializing) return false;
    if (!equal(prevProps.editingCell, nextProps.editingCell)) return false;
    // Workflow variant props
    if (prevProps.variant !== nextProps.variant) return false;
    if (prevProps.hideData !== nextProps.hideData) return false;
    if (!equal(prevProps.placeholderTexts, nextProps.placeholderTexts))
      return false;
    if (!equal(prevProps.promptPills, nextProps.promptPills)) return false;
    if (prevProps.onPromptPillClick !== nextProps.onPromptPillClick)
      return false;
    if (prevProps.modelTier !== nextProps.modelTier) return false;
    if (prevProps.selectedModelKey !== nextProps.selectedModelKey) return false;
    if (prevProps.deepResearch !== nextProps.deepResearch) return false;
    if (prevProps.messages?.length !== nextProps.messages?.length) return false;
    if (prevProps.isAgentProcessing !== nextProps.isAgentProcessing) return false;
    // Replay-gate props: flip read-only / highlight when nextUserMessage arrives.
    if (prevProps.forceReadOnly !== nextProps.forceReadOnly) return false;
    if (prevProps.highlightSend !== nextProps.highlightSend) return false;
    if (prevProps.onCmdEnter !== nextProps.onCmdEnter) return false;

    return true;
  }
);

function PureAttachmentsButton({
  fileInputRef,
  isRerunning,
  isInitializing,
  compact = false,
}: {
  fileInputRef: React.MutableRefObject<HTMLInputElement | null>;
  status: UseChatHelpers["status"];
  isRerunning?: boolean;
  isInitializing?: boolean;
  compact?: boolean;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          data-testid="attachments-button"
          type="button"
          className="flex items-center gap-1.5 px-2 py-1.5 rounded-[8px] text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-all duration-150 disabled:opacity-30 disabled:pointer-events-none"
          onClick={(event) => {
            event.preventDefault();
            fileInputRef.current?.click();
          }}
          disabled={isRerunning || isInitializing}
        >
          <PaperclipIcon size={14} />
          {!compact && <span className="text-[11px] font-medium leading-none whitespace-nowrap">Attach</span>}
        </button>
      </TooltipTrigger>
      {compact && (
        <TooltipContent side="top" className="text-xs px-2 py-1">Attach files</TooltipContent>
      )}
    </Tooltip>
  );
}

const AttachmentsButton = memo(PureAttachmentsButton);

function PureVoiceRecorderButton({
  isDisabled,
  onStart,
}: {
  isDisabled: boolean;
  onStart: () => void;
}) {
  return (
    <Button
      type="button"
      className="rounded-md p-[7px] h-fit border-border hover:bg-muted"
      onClick={(event) => {
        event.preventDefault();
        onStart();
      }}
      disabled={isDisabled}
      variant="ghost"
      title="Record voice message"
    >
      <Mic size={14} />
    </Button>
  );
}

const VoiceRecorderButton = memo(PureVoiceRecorderButton);

function PureStopButton({
  chatId,
  setMessages,
  onStopped,
  onUsageRefresh,
}: {
  chatId: string;
  setMessages: UseChatHelpers["setMessages"];
  onStopped?: () => void;
  onUsageRefresh?: () => void;
}) {
  const { user } = useAuth();
  const [isStopping, setIsStopping] = useState(false);

  const handleCustomStop = async () => {
    if (!user?.uid || isStopping) return;

    setIsStopping(true);
    debugLog("stop-clicked", { chatId, user_id: user.uid });
    telemetry.emit("chat.stop_click", {
      session_id: chatId,
      user_id: user.uid,
    });
    try {
      await apiClient.stopAgent(user.uid, chatId);
      setMessages((messages) => messages);
      onStopped?.();
      window.setTimeout(() => onUsageRefresh?.(), 1200);
      debugLog("stop-sent", { chatId, user_id: user.uid });
      telemetry.emit("chat.stop_success", {
        session_id: chatId,
        user_id: user.uid,
      });
    } catch (error) {
      debugLog("stop-error", {
        chatId,
        user_id: user.uid,
        error: error instanceof Error ? error.message : String(error),
      });
      telemetry.emit("chat.stop_error", {
        session_id: chatId,
        user_id: user.uid,
        error_message: error instanceof Error ? error.message.slice(0, 500) : String(error).slice(0, 500),
      });
      toast.error("Failed to stop the response. Please try again.");
    } finally {
      setIsStopping(false);
    }
  };

  return (
    <Button
      data-testid="stop-button"
      className="rounded-full p-1.5 h-fit border dark:border-zinc-600"
      disabled={isStopping}
      onClick={(event) => {
        event.preventDefault();
        handleCustomStop();
      }}
    >
      {isStopping ? (
        <Loader2 size={14} className="animate-spin" />
      ) : (
        <StopIcon size={14} />
      )}
    </Button>
  );
}

const StopButton = memo(PureStopButton);

function PureSendButton({
  submitForm,
  input,
  uploadQueue,
  isRerunning,
  isInitializing,
  highlightSend,
}: {
  submitForm: () => void;
  input: string;
  uploadQueue: Array<Attachment>;
  isRerunning?: boolean;
  isInitializing?: boolean;
  /**
   * When true, wraps the button in a gentle scale-pulse and swaps the
   * styling to a white-on-black filled button with a soft white glow,
   * so a viewer's eye is drawn to it during a demo replay's "click to
   * continue" beat. Defaults to false — identical to prior behaviour.
   */
  highlightSend?: boolean;
}) {
  const button = (
    <Button
      data-testid="send-button"
      className={cx(
        "rounded-full p-1.5 h-fit border transition-all duration-300",
        highlightSend
          ? "!bg-white !border-white !text-black hover:!bg-zinc-100 shadow-[0_0_0_4px_rgba(255,255,255,0.12),0_0_22px_rgba(255,255,255,0.35)] hover:shadow-[0_0_0_6px_rgba(255,255,255,0.18),0_0_28px_rgba(255,255,255,0.5)]"
          : "dark:border-zinc-600"
      )}
      onClick={(event) => {
        event.preventDefault();
        submitForm();
      }}
      disabled={
        input.trim().length === 0 ||
        uploadQueue.length > 0 ||
        isRerunning ||
        isInitializing
      }
    >
      <ArrowUpIcon size={14} />
    </Button>
  );

  if (!highlightSend) return button;

  // Soft breathing pulse while the replay-gate is open. Intentionally
  // subtle (scale 1 → 1.06) so it registers peripherally without
  // looking like a bouncing CTA. Framer-motion is already imported at
  // the top of this file.
  return (
    <motion.div
      animate={{ scale: [1, 1.06, 1] }}
      transition={{
        duration: 1.6,
        ease: "easeInOut",
        repeat: Infinity,
        repeatDelay: 0.4,
      }}
      className="inline-flex"
    >
      {button}
    </motion.div>
  );
}

const SendButton = memo(PureSendButton, (prevProps, nextProps) => {
  if (prevProps.submitForm !== nextProps.submitForm) return false;
  if (prevProps.uploadQueue.length !== nextProps.uploadQueue.length)
    return false;
  if (prevProps.input !== nextProps.input) return false;
  if (prevProps.isRerunning !== nextProps.isRerunning) return false;
  if (prevProps.isInitializing !== nextProps.isInitializing) return false;
  if (prevProps.highlightSend !== nextProps.highlightSend) return false;
  return true;
});
